import * as React from 'react';
import { PanelType, PrimaryButton, DefaultButton, CommandBarButton, Icon, Selection, IObjectWithKey, IContextualMenuItem, Dialog, DialogType, DialogFooter, MessageBar, MessageBarType, IDetailsRowProps, IRenderFunction, TooltipHost, css } from 'office-ui-fabric-react';
import { Resource, TimephasedRate, TimephasedCost, TimephasedMaxUnits, TimephasedNonProjectWork, actionCreators as ResourceActions } from '../../../store/ResourcesListStore';
import * as Metadata from '../../../entities/Metadata';
import { Dictionary, EntityType, MaybeDate, mapServerEntityType } from '../../../entities/common';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { bindActionCreators } from 'redux';
import EntityDetailsList, { ListGroupingProps, TInlineEditProps } from '../../common/extensibleEntity/EntityDetailsList';
import EntityGroupHeader, { EntityGroup } from '../../common/extensibleEntity/EntityGroupHeader';
import { CostRateTable, actionCreators as CostRateTableActions } from '../../../store/CostRateTableStore';
import GroupSelectionPanel from '../../common/sectionsControl/uiControls/tasksControl/GroupSelectionPanel';
import { GroupInfo } from '../../common/CreateGroupPanel';
import { EntityTypeLabels } from '../../../entities/common';
import SelectionModeSwitchableCommandBar from '../../common/SelectionModeSwitchableCommandBar';
import { toDate } from '../../utils/common';
import { getTimephasedFieldConfig } from '../../../services/TimephasedConfigService';
import { SortService } from '../../../services/SortService';
import { getDefaultCostRateTable } from '../../../store/selectors/costRateTableSelectors';
import { canResourceAllRead } from '../../../store/permissions';
import './TimephasedDataPanel.css';
import { DateFormatter } from '../../common/formatters/DateFormatter';
import ExpandablePanel from '../../common/ExpandablePanel';
import Link from '../../common/Link';

const SAVE_FORM_TIMEOUT = 100;

type OwnProps = {
    isOpen: boolean;
    onDismiss: () => void;
    resource: Resource;
    field: Metadata.Field;
};

type StateProps = {
    fields: Metadata.Field[];
    costRateTables: CostRateTable[];
    defaultCostRateTable?: CostRateTable;
    resourcePlanningLevel: EntityType;
    canManageCostRateTables: boolean;
};

type ActionProps = {
    resourceActions: typeof ResourceActions;
    costRateTableActions: typeof CostRateTableActions;
};

type TimephasedDataPanelProps = OwnProps & StateProps & ActionProps;

type TimephasedEntry = TimephasedRate | TimephasedCost | TimephasedMaxUnits | TimephasedNonProjectWork;

const EFFECTIVE_UNTIL_FAKE_FIELD: Metadata.Field = {
    id: 'EffectiveUntil',
    name: 'EffectiveUntil',
    label: 'Effective Until',
    type: Metadata.FieldType.Text,
    group: Metadata.FieldGroup.SystemFields,
    isNative: false,
    isCustom: false,
    isReadonly: true,
    isSystem: false,
    isFake: true
} as Metadata.Field;

interface EntityDependency {
    entityType: string;
    name: string;
}

interface EntityDependencyWithResources extends EntityDependency {
    resourceNames?: string[];
}

interface DeletionError {
    tableName: string;
    entities: EntityDependencyWithResources[];
    message?: string;
    note?: {
        currentLevel: string;
        hiddenLevels: string;
    };
}

interface TimephasedDataPanelState {
    isManageCostRateTablesOpen: boolean;
    data: TimephasedEntry[];
    deletedIds: string[];
    deletedIdByKey: Dictionary<string>;
    selectedIds: string[];
    hasChanges: boolean;
    deleteError?: DeletionError;
}

export function getTimephasedConfig(fieldName: string) {
    const baseConfig = getTimephasedFieldConfig(fieldName);
    if (!baseConfig) {
        return null;
    }

    const uiConfigMap: Record<EntityType.TimephasedRate | EntityType.TimephasedCost | EntityType.TimephasedMaxUnits | EntityType.TimephasedNonProjectWork, {
        title: string;
        description: React.ReactNode;
    }> = {
        [EntityType.TimephasedRate]: {
            title: 'Effective Rates',
            description: <>
                Define effective periods and values for Standard Rate, Overtime Rate, and Charge per Use. Please refer to the <Link target="_blank" href="https://help.ppm.express/111183-resource-management/effective-costs">article</Link> for more details.
            </>,
        },
        [EntityType.TimephasedCost]: {
            title: 'Effective Costs',
            description: <>
                Define effective periods and values for Standard Cost, Overtime Cost, and Cost per Use. Please refer to the <Link target="_blank" href="https://help.ppm.express/111183-resource-management/effective-costs">article</Link> for more details.
            </>,
        },
        [EntityType.TimephasedMaxUnits]: {
            title: 'Effective Max Units',
            description: 'Define effective periods and values for Max Units %.',
        },
        [EntityType.TimephasedNonProjectWork]: {
            title: 'Effective Non-Project Work',
            description: 'Define effective periods and values for Non-Project Work %.',
        }
    };

    return {
        ...baseConfig,
        ...uiConfigMap[baseConfig.entityType]
    };
}

class TimephasedDataPanelComponent extends React.Component<TimephasedDataPanelProps, TimephasedDataPanelState> {
    private _selection: Selection;
    private _currentPeriodIdsCache?: { dataRef: TimephasedEntry[]; ids: Set<string> };

    constructor(props: TimephasedDataPanelProps) {
        super(props);
        this._selection = new Selection({
            onSelectionChanged: () => {
                const selected = this._selection.getSelection() as TimephasedEntry[];
                this.setState({ selectedIds: selected.map(_ => _.id) });
            },
            getKey: (item: IObjectWithKey) => (item as TimephasedEntry).id
        });
        this.state = {
            isManageCostRateTablesOpen: false,
            data: this.getTimephasedData(),
            deletedIds: [],
            deletedIdByKey: {},
            selectedIds: [],
            hasChanges: false,
            deleteError: undefined,
        };
    }

    componentDidUpdate(prevProps: TimephasedDataPanelProps) {
        if (prevProps.resource !== this.props.resource) {
            this._selection.setAllSelected(false);
            this.setState({ data: this.getTimephasedData(), deletedIds: [], deletedIdByKey: {}, selectedIds: [], hasChanges: false, isManageCostRateTablesOpen: false });
        }

        if (prevProps.field?.name !== this.props.field?.name) {
            this._selection.setAllSelected(false);
            this.setState({ data: this.getTimephasedData(), deletedIds: [], deletedIdByKey: {}, selectedIds: [], hasChanges: false, isManageCostRateTablesOpen: false });
        }
    }

    private buildDeletedKey = (groupKey: string, effectiveFrom: MaybeDate): string => {
        return `${groupKey}|${toDate(effectiveFrom)?.getTime() ?? ''}`;
    }

    private resolveCostRateTableId = (item: TimephasedEntry): string | undefined => {
        return item.attributes?.CostRateTable?.id ?? this.props.defaultCostRateTable?.id ?? this.props.costRateTables[0]?.id;
    }

    private resolveGroupKey = (item: TimephasedEntry): string => {
        const config = this.getConfig();
        if (!config?.supportsCostRateTable) {
            return 'ungrouped';
        }

        return this.resolveCostRateTableId(item) ?? 'ungrouped';
    }

    private isBoundToDeletedCostRateTable = (item: TimephasedEntry): boolean => {
        const config = this.getConfig();
        if (!config?.supportsCostRateTable) {
            return false;
        }

        const costRateTableId = item.attributes?.CostRateTable?.id;
        if (!costRateTableId) {
            return false;
        }

        return !this.props.costRateTables.some(_ => _.id === costRateTableId);
    }

    private isDefaultPeriod = (item: TimephasedEntry): boolean => {
        return toDate(item.attributes?.EffectiveFrom) == null;
    }

    private isEffectiveFromUniqueInGroup = (item: TimephasedEntry, effectiveFromValue: MaybeDate, data: TimephasedEntry[]): boolean => {
        const normalizedValue = toDate(effectiveFromValue)?.getTime() ?? null;

        const groupKey = this.resolveGroupKey(item);

        for (const entry of data) {
            if (entry.id === item.id) {
                continue;
            }

            const otherGroupKey = this.resolveGroupKey(entry);

            if (otherGroupKey !== groupKey) {
                continue;
            }

            const other = toDate(entry.attributes?.EffectiveFrom)?.getTime() ?? null;
            if (other === normalizedValue) {
                return false;
            }
        }

        return true;
    }

    private getValidationError = (): string | undefined => {
        const config = this.getConfig();
        if (!config) {
            return undefined;
        }

        for (const item of this.state.data) {
            if (!this.isEffectiveFromUniqueInGroup(item, item.attributes?.EffectiveFrom, this.state.data)) {
                if (!item.attributes?.EffectiveFrom) {
                    return 'Effective From must be unique in the Cost Rate Table. Remove duplicate row with N/A.';
                }
                return 'Effective From must be unique in the Cost Rate Table.';
            }
        }

        const groupKeys = new Set(this.state.data.map(_ => this.resolveGroupKey(_)));
        for (const groupKey of groupKeys) {
            const groupItems = this.getGroupItemsSorted(this.state.data, groupKey);
            if (!groupItems.length) {
                continue;
            }

            const hasDefaultPeriod = groupItems.some(_ => this.isDefaultPeriod(_));
            if (!hasDefaultPeriod) {
                const groupName = groupKey === 'ungrouped'
                    ? 'Default'
                    : (this.props.costRateTables.find(t => t.id === groupKey)?.name ?? 'this group');

                return `Default period (N/A Effective From) is required for "${groupName}". Clear the Effective From date to set it to N/A.`;
            }
        }

        return undefined;
    }

    private sortTimephasedData = (data: TimephasedEntry[]): TimephasedEntry[] => {
        const config = this.getConfig();
        if (!config?.supportsCostRateTable) {
            return [...data].sort((a, b) => SortService.compareDates(a.attributes?.EffectiveFrom, b.attributes?.EffectiveFrom));
        }

        const groupOrder = this.props.costRateTables.map(t => t.id);

        return [...data].sort((a, b) => {
            const groupA = this.resolveCostRateTableId(a);
            const groupB = this.resolveCostRateTableId(b);

            const idxA = groupA ? groupOrder.indexOf(groupA) : -1;
            const idxB = groupB ? groupOrder.indexOf(groupB) : -1;

            if (idxA !== idxB) {
                return idxA - idxB;
            }

            return SortService.compareDates(a.attributes?.EffectiveFrom, b.attributes?.EffectiveFrom);
        });
    }

    private getConfig() {
        return getTimephasedConfig(this.props.field.name);
    }

    private getTimephasedData(): TimephasedEntry[] {
        const config = this.getConfig();
        if (!config) {
            return [];
        }
        const data = (this.props.resource[config.collectionKey as keyof Resource] as TimephasedEntry[]) || [];
        return this.sortTimephasedData(data);
    }

    private getGroupItemsSorted = (data: TimephasedEntry[], groupKey: string): TimephasedEntry[] => {
        const config = this.getConfig();
        const groupItems = data.filter(entry => {
            if (!config?.supportsCostRateTable) {
                return true;
            }

            const resolved = this.resolveCostRateTableId(entry);
            return resolved === groupKey;
        });

        return this.sortTimephasedData(groupItems);
    }

    private getNewEntryDefaultsForGroup = (prevStateData: TimephasedEntry[], groupKey: string) => {
        const config = this.getConfig();

        const groupItemsSorted = this.getGroupItemsSorted(prevStateData, groupKey);
        const previousRow = groupItemsSorted.length > 0
            ? groupItemsSorted[groupItemsSorted.length - 1]
            : undefined;

        // Find the cost rate table for this group (Rates/Costs only)
        const costRateTable = config?.supportsCostRateTable
            ? (this.props.costRateTables.find(t => t.id === groupKey) ?? this.props.defaultCostRateTable ?? this.props.costRateTables[0] ?? null)
            : null;

        const today = toDate(new Date().toDateOnlyString())!;

        let nextRowEffectiveFrom: Date | null = null;

        if (previousRow && previousRow.attributes) {
            const entryDate = toDate(previousRow.attributes.EffectiveFrom);

            nextRowEffectiveFrom = entryDate && entryDate >= today
                ? new Date(entryDate).addDays(1)
                : today;
        }

        const defaultAttributes: Dictionary<any> = {};
        for (const field of this.props.fields) {
            if (field.name === 'EffectiveFrom' || field.name === 'CostRateTable' || field.name === 'EffectiveUntil') {
                continue;
            }

            const isNumeric = field.type === Metadata.FieldType.Integer || field.type === Metadata.FieldType.Decimal;
            if (!isNumeric) {
                continue;
            }

            const prevValue = previousRow?.attributes?.[field.name];
            defaultAttributes[field.name] = previousRow ? (prevValue ?? 0) : 0;
        }

        const attributes: Dictionary<any> = {
            EffectiveFrom: nextRowEffectiveFrom,
            CostRateTable: costRateTable ? {
                id: costRateTable.id,
                name: costRateTable.name,
                color: costRateTable.color,
            } : null,
            ...defaultAttributes
        };

        return {
            nextRowEffectiveFrom,
            attributes,
        };
    }

    private getGroups(): EntityGroup[] {
        const { costRateTables } = this.props;
        const { data } = this.state;
        const config = this.getConfig();

        // Only group by cost rate table for Rates and Costs
        const hasCostRateTableSupport = config?.supportsCostRateTable ?? false;

        if (!hasCostRateTableSupport || costRateTables.length === 0) {
            // Single default group for MaxUnits and NonProjectWork
            return [{
                key: 'ungrouped',
                name: 'Default',
                color: '#788086',
                count: data.length,
                data: undefined
            }];
        }

        // Create groups from cost rate tables
        const groups = costRateTables.map(table => {
            const groupData = data.filter(entry => this.resolveCostRateTableId(entry) === table.id);

            return {
                key: table.id,
                name: table.isDefault ? `${table.name} (default)` : table.name,
                color: table.color,
                count: groupData.length,
                data: { table },
                title: table.description
            };
        });

        return groups;
    }

    private getListGrouping(): ListGroupingProps {
        return {
            getGroupKey: (item: TimephasedEntry) => this.resolveGroupKey(item),
            renderGroupHeader: (group, isCollapsed, isSelected, onToggleCollapse, onToggleSelect) => {
                return (
                    <EntityGroupHeader
                        group={group}
                        isCollapsed={isCollapsed}
                        isSelected={isSelected}
                        onToggleCollapse={onToggleCollapse}
                        onToggleSelect={onToggleSelect}

                    />
                );
            },
            renderGroupFooter: (group) => {
                return (
                    <div className="with-group-marker group-footer align-center">
                        <div className="group-marker" style={{ backgroundColor: group.color }} />
                        <div className="container">
                            <CommandBarButton
                                iconProps={{ iconName: 'Add' }}
                                text="Add Row"
                                onClick={() => this.handleAddRowForGroup(group.key)}
                                styles={{
                                    root: { height: 40 },
                                    icon: { color: '#128b10' }
                                }}
                            />
                        </div>
                    </div>
                );
            }
        };
    }

    private getDisplayFields(): string[] {
        const fieldNames = this.props.fields.map(f => f.name);

        // Add EffectiveFrom at the beginning if not already present
        if (!fieldNames.includes('EffectiveFrom')) {
            fieldNames.unshift('EffectiveFrom');
        }

        // Add EffectiveUntil after EffectiveFrom
        if (!fieldNames.includes('EffectiveUntil')) {
            const effectiveFromIndex = fieldNames.indexOf('EffectiveFrom');
            fieldNames.splice(effectiveFromIndex + 1, 0, 'EffectiveUntil');
        }

        // Remove CostRateTable column
        if (fieldNames.includes('CostRateTable')) {
            fieldNames.splice(fieldNames.indexOf('CostRateTable'), 1);
        }

        return fieldNames;
    }

    private getFakeFields(): Metadata.Field[] {
        return [EFFECTIVE_UNTIL_FAKE_FIELD];
    }

    private resolveEffectiveUntil = (item: TimephasedEntry): Date | undefined => {
        const groupKey = this.resolveGroupKey(item);

        // Get all items in the same group, sorted by effectiveFrom
        const groupItems = this.state.data
            .filter(entry => this.resolveGroupKey(entry) === groupKey)
            .filter(entry => entry.attributes?.EffectiveFrom) // Only items with effectiveFrom
            .sort((a, b) => SortService.compareDates(a.attributes?.EffectiveFrom, b.attributes?.EffectiveFrom));

        // Find current item index
        const currentIndex = groupItems.findIndex(entry => entry.id === item.id);

        // If this is the last item in the group, return N/A
        if (currentIndex === groupItems.length - 1) {
            return undefined;
        }

        // Get the next item's effectiveFrom date
        const nextItem = groupItems[currentIndex + 1];
        if (!nextItem.attributes?.EffectiveFrom) {
            return undefined;
        }

        // Calculate the day before next item's effectiveFrom
        const nextDate = new Date(nextItem.attributes.EffectiveFrom);
        const untilDate = new Date(nextDate).addDays(-1);

        return untilDate;
    };

    private handleSave = () => {
        setTimeout(() => this.saveChanges(), SAVE_FORM_TIMEOUT);
    };

    private saveChanges = () => {
        const config = this.getConfig();
        if (!config) {
            return;
        }

        const dataToSave = this.state.data.filter(_ => !this.isBoundToDeletedCostRateTable(_));

        if (dataToSave.length !== this.state.data.length) {
            this.setState(prevState => ({
                data: dataToSave,
                selectedIds: prevState.selectedIds.filter(id => dataToSave.some(_ => _.id === id))
            }));
        }

        switch (config.entityType) {
            case EntityType.TimephasedRate:
                this.props.resourceActions.updateTimephasedRates(this.props.resource.id, dataToSave as TimephasedRate[], this.state.deletedIds);
                break;
            case EntityType.TimephasedCost:
                this.props.resourceActions.updateTimephasedCosts(this.props.resource.id, dataToSave as TimephasedCost[], this.state.deletedIds);
                break;
            case EntityType.TimephasedMaxUnits:
                this.props.resourceActions.updateTimephasedMaxUnits(this.props.resource.id, dataToSave as TimephasedMaxUnits[], this.state.deletedIds);
                break;
            case EntityType.TimephasedNonProjectWork:
                this.props.resourceActions.updateTimephasedNonProjectWork(this.props.resource.id, dataToSave as TimephasedNonProjectWork[], this.state.deletedIds);
                break;
            default:
                break;
        }

        this.props.onDismiss();
    };

    private handleAddRowForGroup = (groupKey: string) => {
        this.setState(prevState => {
            const { nextRowEffectiveFrom, attributes } = this.getNewEntryDefaultsForGroup(prevState.data, groupKey);
            const key = this.buildDeletedKey(groupKey, nextRowEffectiveFrom);
            const restoredId = prevState.deletedIdByKey[key];

            const newEntry: TimephasedEntry = {
                id: restoredId ?? `temp-${Date.now()}`,
                attributes: attributes as TimephasedEntry['attributes']
            };

            const deletedIdByKey = { ...prevState.deletedIdByKey };
            let deletedIds = prevState.deletedIds;
            if (restoredId) {
                delete deletedIdByKey[key];
                deletedIds = prevState.deletedIds.filter(id => id !== restoredId);
            }

            return {
                data: this.sortTimephasedData([...prevState.data, newEntry]),
                deletedIds,
                deletedIdByKey,
                hasChanges: true,
            };
        });
    };

    private handleInlineEditComplete = async (field: Metadata.Field, item: TimephasedEntry, value: any, extraUpdates?: Dictionary<any>) => {
        const normalizedValue = field.name === 'EffectiveFrom'
            ? toDate(value) ?? null
            : value;

        this.setState(prevState => {
            const config = this.getConfig();

            const isTempId = item.id.toString().startsWith('temp-');
            const groupKey = config?.supportsCostRateTable
                ? (this.resolveCostRateTableId(item) ?? 'ungrouped')
                : 'ungrouped';

            const key = field.name === 'EffectiveFrom'
                ? this.buildDeletedKey(groupKey, normalizedValue)
                : undefined;
            const restoredId = isTempId && key ? prevState.deletedIdByKey[key] : undefined;

            const updated = prevState.data.map(entry => {
                if (entry.id !== item.id) {
                    return entry;
                }

                return {
                    ...entry,
                    id: restoredId ?? entry.id,
                    attributes: {
                        ...entry.attributes,
                        [field.name]: normalizedValue,
                        ...extraUpdates
                    }
                };
            });

            const deletedIdByKey = { ...prevState.deletedIdByKey };
            let deletedIds = prevState.deletedIds;
            if (restoredId && key) {
                delete deletedIdByKey[key];
                deletedIds = prevState.deletedIds.filter(id => id !== restoredId);
            }

            return {
                data: field.name === 'EffectiveFrom' ? this.sortTimephasedData(updated) : updated,
                deletedIds,
                deletedIdByKey,
                hasChanges: true,
            };
        });
    };

    private getInlineEditProps(): TInlineEditProps<TimephasedEntry> {
        return {
            onInlineEditComplete: this.handleInlineEditComplete,
            readonlyFields: ['EffectiveUntil'],
        };
    }

    private handleDeleteSelected = () => {
        const selected = this._selection.getSelection() as TimephasedEntry[];
        if (!selected.length) {
            return;
        }

        const selectedIdsSet = new Set(selected.map(_ => _.id));

        this.setState(prevState => {
            const newData = prevState.data.filter(entry => !selectedIdsSet.has(entry.id));

            const deletedItems = selected.filter(item => !item.id.toString().startsWith('temp-'));
            const deletedIdsToAdd = deletedItems.map(item => item.id);

            const deletedIdByKey = { ...prevState.deletedIdByKey };
            for (const item of deletedItems) {
                const groupKey = this.resolveGroupKey(item);
                const effectiveFrom = toDate(item.attributes?.EffectiveFrom);
                const key = this.buildDeletedKey(groupKey, effectiveFrom);
                deletedIdByKey[key] = item.id;
            }

            const deletedIdsSet = new Set([...prevState.deletedIds, ...deletedIdsToAdd]);

            return {
                data: newData,
                deletedIds: Array.from(deletedIdsSet),
                deletedIdByKey,
                selectedIds: [],
                hasChanges: true
            };
        }, () => this._selection.setAllSelected(false));
    };

    private _getSelectionModeCommands(): IContextualMenuItem[] {
        return [
            {
                key: 'deleteRows',
                name: 'Delete',
                iconProps: { iconName: 'Delete' },
                disabled: this.state.selectedIds.length >= this.state.data.length,
                onClick: () => this.handleDeleteSelected()
            }
        ];
    }

    private renderItem = (entity: TimephasedEntry, index: number, field: Metadata.Field, defaultRender: () => JSX.Element): JSX.Element => {
        if (field.name === 'EffectiveUntil') {
            const until = this.resolveEffectiveUntil(entity);
            return !until
                ? (
                    <TooltipHost content={'Effective Until: N/A means the period is indefinite (no end date, effective indefinitely)'}>
                        <div>N/A</div>
                    </TooltipHost>
                )
                : <DateFormatter value={until} />;
        }

        if (field.name === 'EffectiveFrom') {
            const isCurrentPeriod = this.getCurrentPeriodIds().has(entity.id);
            const content = entity.attributes?.EffectiveFrom
                ? defaultRender()
                : (
                    <TooltipHost content={'Effective From: N/A means the period is indefinite (no start date, effective from the beginning)'}>
                        <div>N/A</div>
                    </TooltipHost>
                );

            return (
                <div className="timephased-effectivefrom">
                    <div className="timephased-effectivefrom__marker">
                        {isCurrentPeriod ? (
                            <Icon iconName="FavoriteStarFill" className="timephased-effectivefrom__star" title="Current Effective Period" />
                        ) : (
                            <span />
                        )}
                    </div>
                    {content}
                </div>
            );
        }

        return defaultRender();
    };

    private getCurrentPeriodIds = (): Set<string> => {
        if (this._currentPeriodIdsCache?.dataRef === this.state.data) {
            return this._currentPeriodIdsCache.ids;
        }

        const today = toDate(new Date().toDateOnlyString())!;

        const grouped: Record<string, TimephasedEntry[]> = {};

        for (const entry of this.state.data) {
            const groupKey = this.resolveCostRateTableId(entry) ?? 'ungrouped';
            grouped[groupKey] ||= [];
            grouped[groupKey].push(entry);
        }

        const ids = new Set<string>();

        for (const groupKey of Object.keys(grouped)) {
            const groupItems = [...grouped[groupKey]].sort((a, b) => SortService.compareDates(a.attributes?.EffectiveFrom, b.attributes?.EffectiveFrom));

            let currentId: string | undefined;
            for (let i = 0; i < groupItems.length; i++) {
                const item = groupItems[i];
                const effectiveFrom = toDate(item.attributes?.EffectiveFrom);
                const fromOk = !effectiveFrom || effectiveFrom <= today;
                if (!fromOk) {
                    continue;
                }

                const nextItem = i < groupItems.length - 1 ? groupItems[i + 1] : undefined;
                const nextDateEffectiveFrom = toDate(nextItem?.attributes?.EffectiveFrom) ;
                const beforeNext = !nextDateEffectiveFrom || nextDateEffectiveFrom > today;
                if (beforeNext) {
                    currentId = item.id;
                }
            }

            if (currentId) {
                ids.add(currentId);
            }
        }

        this._currentPeriodIdsCache = { dataRef: this.state.data, ids };
        return ids;
    };


    render() {
        const { isOpen, onDismiss } = this.props;
        const config = this.getConfig();
        if (!config) {
            return null;
        }

        const validationError = this.getValidationError();
        const groups = this.getGroups();
        const isSaveDisabled = !!validationError || !this.state.hasChanges;

        return (
            <ExpandablePanel
                isOpen={isOpen}
                onDismiss={onDismiss}
                type={PanelType.custom}
                customWidth="1000px"
                onRenderHeader={() => (
                    <div className="ms-Panel-header">
                        <p className="ms-Panel-headerText">Configure {config.title}</p>
                        <div className='ms-Panel-secondaryText'>{config.description}</div>
                    </div>
                )}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => (
                    <div className="timephased-panel__footer">
                        <PrimaryButton onClick={this.handleSave} text="Save" disabled={isSaveDisabled} styles={{ root: { backgroundColor: '#128b10' } }} />
                        <DefaultButton onClick={onDismiss} text="Cancel" />
                        {validationError && (<MessageBar messageBarType={MessageBarType.error}>{validationError}</MessageBar>)}
                    </div>
                )}
            >
                <div className="timephased-panel__content">
                    <div className="timephased-panel__commandbar">
                        <SelectionModeSwitchableCommandBar
                            items={[]}
                            farItems={config.supportsCostRateTable
                                ? [
                                    {
                                        key: 'manageCostRateTables',
                                        text: 'Manage Cost Rate Tables',
                                        iconProps: { iconName: 'Settings' },
                                        disabled: !this.props.canManageCostRateTables,
                                        onClick: () => this.setState({ isManageCostRateTablesOpen: true })
                                    }
                                ]
                                : []}
                            selectionMode={{
                                enabled: this.state.selectedIds.length > 0,
                                items: this._getSelectionModeCommands(),
                                onCancel: () => this._selection.setAllSelected(false)
                            }}
                        />
                    </div>

                    <EntityDetailsList
                        entities={this.state.data}
                        displayFields={this.getDisplayFields()}
                        fields={[...this.props.fields, ...this.getFakeFields()]}
                        entityType={config.entityType}
                        groups={groups}
                        listGrouping={this.getListGrouping()}
                        disableNavigation={true}
                        isFilterApplied={false}
                        inlineEditProps={this.getInlineEditProps()}
                        selection={this._selection}
                        onItemRender={this.renderItem}
                        fabricListProps={{
                            onRenderRow: (props?: IDetailsRowProps, defaultRender?: IRenderFunction<IDetailsRowProps>) => {
                                if (!props || !defaultRender) {
                                    return null;
                                }

                                const isCurrentPeriod = this.getCurrentPeriodIds().has((props.item as TimephasedEntry).id);
                                return defaultRender({ ...props, className: css(props.className, isCurrentPeriod && "timephased-row--current") });
                            }
                        }}
                    />
                </div>

                {this.state.isManageCostRateTablesOpen && (
                    <GroupSelectionPanel
                        groups={this.props.costRateTables.map(table => ({
                            id: table.id,
                            name: table.name,
                            color: table.color,
                            description: table.description,
                            isShown: true,
                            isDefault: table.isDefault
                        }))}
                        onDismiss={() => {
                            if (!this.state.deleteError) {
                                this.setState({ isManageCostRateTablesOpen: false })
                            }
                        }}
                        allowCreateGroups={true}
                        allowEditGroups={true}
                        saveGroup={this.handleSaveCostRateTable}
                        removeGroup={(group) => this.handleRemoveCostRateTable(group.id)}
                        reorderGroups={this.handleReorderCostRateTables}
                        entityName={{ singular: 'Cost Rate Table', plural: 'Cost Rate Tables' } as EntityTypeLabels}
                        disableDeleteForDefault={true}
                        hideIsDefault={false}
                        hideToggle={true}
                        showDescription={true}
                        itemClassName="with-hover"
                        panelDescription={
                            <>
                                Create multiple cost rate tables to apply different rates for specific tasks or resource plans.
                                Please refer to <Link target="_blank" href="https://help.ppm.express/111183-resource-management/cost-rate-tables">this article</Link> for more details.
                                <div className="message-bar">
                                    <MessageBar messageBarType={MessageBarType.warning}>
                                        Changes to settings of cost rate tables apply tenant-wide and will affect all resources using this table.
                                    </MessageBar>
                                </div>
                            </>
                        }
                        createPanelDescription={
                            <>
                                Configure the Cost Rate Table settings.
                                <div className="message-bar">
                                    <MessageBar messageBarType={MessageBarType.warning}>
                                        Changes to settings of cost rate tables apply tenant-wide and will affect all resources using this table.
                                    </MessageBar>
                                </div>
                            </>
                        }
                    />
                )}

                {this.state.deleteError && (
                    <Dialog
                        hidden={false}
                        maxWidth={500}
                        onDismiss={() => this.setState({ deleteError: undefined })}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: 'Unable to Delete Cost Rate Table'
                        }}
                    >
                        <div className="timephased-delete-dialog__text">
                            {this.state.deleteError.entities.length > 0 ? (
                                <>
                                    <p className="timephased-delete-dialog__paragraph">
                                        The cost rate table "{this.state.deleteError.tableName}" is in use:
                                    </p>
                                    {this.renderEntityDependencies(this.state.deleteError.entities)}
                                    <p className="timephased-delete-dialog__paragraph--top">
                                        Update the resources in the entity(s) to use a different cost rate table before deleting the current one.
                                    </p>
                                    {this.state.deleteError.note && (
                                        <MessageBar messageBarType={MessageBarType.warning}>
                                            NOTE: Resource plans are currently available on the {this.state.deleteError.note.currentLevel} level but there are hidden resource plans on the {this.state.deleteError.note.hiddenLevels}. To update resources in the hidden resource plans temporarily switch the resource plan level in the Tenant Settings.
                                        </MessageBar>
                                    )}
                                </>
                            ) : (
                                <p>{this.state.deleteError.message ?? 'Unable to delete cost rate table'}</p>
                            )}
                        </div>
                        <DialogFooter>
                            <PrimaryButton onClick={() => this.setState({ deleteError: undefined })} text="Got it" />
                        </DialogFooter>
                    </Dialog>
                )}
            </ExpandablePanel>
        );
    }

    private handleSaveCostRateTable = (groupInfo: GroupInfo) => {
        if (groupInfo.id) {
            this.props.costRateTableActions.updateCostRateTable(
                groupInfo.id,
                groupInfo.name,
                groupInfo.description ?? '',
                groupInfo.color,
                groupInfo.isDefault
            );
        } else {
            this.props.costRateTableActions.createCostRateTable(
                groupInfo.name,
                groupInfo.description ?? '',
                groupInfo.color,
                groupInfo.isDefault
            );
        }
    };

    private renderEntityDependencies = (entities: EntityDependencyWithResources[]) => {
        return entities.map((entity, index) => (
            <p
                key={index}
                className="timephased-delete-dialog__dependency"
            >
                - {entity.entityType} "{entity.name}"; {entity.resourceNames && entity.resourceNames.length > 0
                    ? `${entity.resourceNames.length > 1 ? 'Resource(s)' : 'Resource'} ${entity.resourceNames.join(', ')}`
                    : 'Resource(s)'}
            </p>
        ));
    };

    private handleRemoveCostRateTable = (tableId: string) => {
        this.props.costRateTableActions.deleteCostRateTable(tableId, (result) => {
            if (!result.message || !result.dependendencies?.length) {
                this.setState({
                    deleteError: {
                        tableName: result.name,
                        entities: [],
                        message: result.message
                    }
                });
                return;
            }

            const entities: EntityDependencyWithResources[] = result.dependencyDetails && result.dependencyDetails.length > 0
                ? result.dependencyDetails.map(d => ({
                    entityType: d.entityType,
                    name: d.entityName,
                    resourceNames: d.resourceNames
                }))
                : result.message
                    .split('|')
                    .map(pair => {
                        const [entityType, name] = pair.split(':');
                        return { entityType, name };
                    });

            const currentPlanningLevel = (this.props.resourcePlanningLevel ?? '').toString().toLowerCase();
            const entityTypesRaw = [...new Set(entities.map(e => e.entityType).filter(Boolean))];
            const nonCurrentLevels = entityTypesRaw.filter(t => {
                const level = t.toString().toLowerCase();
                return level !== currentPlanningLevel && level !== EntityType.Idea.toLowerCase();
            });

            let note: { currentLevel: string; hiddenLevels: string } | undefined;
            if (nonCurrentLevels.length > 0) {
                const levelsText = nonCurrentLevels.length === 1
                    ? `${nonCurrentLevels[0]} level`
                    : nonCurrentLevels.map(l => `${l} level`).join(' and ');

                note = {
                    currentLevel: this.props.resourcePlanningLevel,
                    hiddenLevels: levelsText
                };
            }

            this.setState({
                deleteError: {
                    tableName: result.name,
                    entities,
                    message: result.message,
                    note
                }
            });
        });
    };

    private handleReorderCostRateTables = (groups: Metadata.Group[]) => {
        this.props.costRateTableActions.reorderCostRateTables(groups.map(g => g.id));
    };
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    const config = getTimephasedConfig(ownProps.field.name);
    const entityType = config?.entityType ?? EntityType.TimephasedRate;

    return {
        fields: Object.values(state.fields[entityType]?.byId || {}),
        costRateTables: state.costRateTables.allIds.map(id => state.costRateTables.byId[id]),
        defaultCostRateTable: getDefaultCostRateTable(state),
        resourcePlanningLevel: mapServerEntityType[state.tenant.resourcePlanningSettings?.resourcePlanningLevel!]!,
        canManageCostRateTables: canResourceAllRead(state.user.permissions.resource)
    };
}

function mapDispatchToProps(dispatch: any): ActionProps {
    return {
        resourceActions: bindActionCreators(ResourceActions, dispatch),
        costRateTableActions: bindActionCreators(CostRateTableActions, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(TimephasedDataPanelComponent);

